import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Users,
  Clapperboard,
  Receipt,
  UserCircle,
  Calendar,
  Settings,
  Menu,
  X,
  Camera,
  ClipboardList,
  RefreshCcw,
  LogOut,
  Truck,
  CreditCard,
  Shield,
  MoreHorizontal,
  Clock,
  FolderKanban,
  Wallet,
  FileText,
  Landmark,
  MapPin,
  Files,
  MessageSquareText,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { canViewFinance, canBill } from "@/lib/financeUtils";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/models", label: "Models", icon: Camera },
  { path: "/inventory", label: "Inventory", icon: Package },
  { path: "/team", label: "Team", icon: Users },
  { path: "/tasks", label: "Tasks", icon: ClipboardList },
  { path: "/projects", label: "Projects", icon: FolderKanban },
  { path: "/finance", label: "Finance", icon: Wallet, financeGated: true },
  { path: "/billing", label: "Billing", icon: FileText, billingGated: true },
  { path: "/parties", label: "คู่ค้า", icon: Users, adminOnly: true },
  { path: "/tax-reports", label: "รายงานภาษี", icon: Landmark, adminOnly: true },
  { path: "/refund", label: "Refund", icon: RefreshCcw },
  { path: "/logistic", label: "Logistic", icon: Truck },
  { path: "/venue", label: "Venue", icon: MapPin },
  { path: "/documents", label: "Documents", icon: Files },
  { path: "/chat-guide", label: "Chat Guide", icon: MessageSquareText },
  { path: "/customer-payments", label: "Customer Pay", icon: CreditCard, adminOnly: true },
  { path: "/staff-line", label: "Staff LINE", icon: Shield, adminOnly: true },
  { path: "/profile", label: "My Profile", icon: Clock },
];

export const Sidebar = ({ externalOpen, onExternalClose }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isOpen = mobileOpen || externalOpen;
  const handleClose = () => { setMobileOpen(false); onExternalClose?.(); };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile menu button — only show when bottom nav isn't present (i.e., hidden) */}
      <button
        data-testid="mobile-menu-btn"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl bg-white shadow-md border border-gray-100"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {isOpen ? <X size={20} className="text-gray-700" /> : <Menu size={20} className="text-gray-700" />}
      </button>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={handleClose}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-screen w-64 bg-[#1a1a1a] z-40 transform transition-transform duration-200 ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_bizops-manager-3/artifacts/3f35f7ea_LOGO_FYM-02.png" 
              alt="FLASHYOURMEME" 
              className="h-8 w-auto"
            />
            <span className="font-bold text-white text-sm tracking-tight">FLASHYOURMEME</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.filter((item) => {
            if (item.financeGated) return canViewFinance(user);
            if (item.billingGated) return canBill(user);
            return !item.adminOnly || user?.role === "admin";
          }).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={handleClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? "bg-[#BFFF00] text-black"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon size={18} strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 space-y-2">
          {/* User Info */}
          {user && (
            <div className="flex items-center gap-3 px-4 py-2 text-sm">
              <div className="w-8 h-8 rounded-full bg-[#BFFF00]/20 flex items-center justify-center">
                <UserCircle size={18} className="text-[#BFFF00]" />
              </div>
              <div>
                <p className="text-white font-medium">{user.name}</p>
                <p className="text-gray-500 text-xs">{user.role === 'admin' ? 'Admin' : 'User'}</p>
              </div>
            </div>
          )}
          
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            onClick={handleClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                isActive
                  ? "bg-[#BFFF00] text-black"
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Settings size={18} strokeWidth={1.5} />
            Settings
          </NavLink>
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 text-gray-400 hover:text-red-400 hover:bg-red-400/10 w-full"
          >
            <LogOut size={18} strokeWidth={1.5} />
            ออกจากระบบ
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

// ─── Bottom Tab Bar (mobile only) ─────────────────────────────
const bottomTabs = [
  { path: "/",         label: "หน้าหลัก",  icon: LayoutDashboard },
  { path: "/refund",   label: "Refund",    icon: RefreshCcw },
  { path: "/profile", label: "โปรไฟล์", icon: Clock },
  { path: "/tasks",    label: "Tasks",     icon: ClipboardList },
];

export function BottomNav({ onMoreClick }) {
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#1a1a1a] border-t border-white/10 flex items-stretch safe-area-bottom"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
      {bottomTabs.map((tab) => (
        <NavLink
          key={tab.path}
          to={tab.path}
          end={tab.path === "/"}
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors ${
              isActive ? "text-[#BFFF00]" : "text-gray-500"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <tab.icon size={20} strokeWidth={isActive ? 2 : 1.5} />
              <span>{tab.label}</span>
            </>
          )}
        </NavLink>
      ))}
      {/* More button */}
      <button
        onClick={onMoreClick}
        className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium text-gray-500 active:text-white transition-colors"
      >
        <MoreHorizontal size={20} strokeWidth={1.5} />
        <span>เพิ่มเติม</span>
      </button>
    </nav>
  );
}
